import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import { OptionIcon } from '@app/src/components/Form/Select'
import { ListboxComponent } from '@app/src/components/Form/SelectVirtualList'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { Organization } from '@app/src/types/organizations'
import { NotificationSeverity, ONE_MINUTE_MS } from '@app/src/wf-constants'
import { Autocomplete, Chip, Divider, Grid, List, ListItem, ListItemText, TextField } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

const TopicalRiskConfig: React.FC = () => {
  const { formatMessage } = useIntl()
  const { mutateAsync } = useCreateResource()
  const { showSnackbar } = useSnackbar()
  const [loadingAllOrgs, setLoadingAllOrgs] = useState(false)
  const [loadingSpecificOrgs, setLoadingSpecificOrgs] = useState(false)
  const [selectedOrganizations, setSelectedOrganizations] = useState<Organization[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchInput])

  const { items: organizations, isLoading: isLoadingOrganizations } = useFetchCollectionWithPost<Organization>({
    endpoint: endpoints.organizationsCollection,
    key: FetchKey.Organization,
    payload: {
      filter: [
        ...(debouncedSearch
          ? [{ name: 'name', filters: [{ operator: Operators.Contains, value: debouncedSearch }] }]
          : []),
      ],
      sort: { target: 'name', order: SortOrder.ASCENDING },
      pagination: { itemsPerPage: 100, pageNumber: 1 },
      include: [],
    },
    options: {
      staleTime: ONE_MINUTE_MS,
    },
  })

  const handleRunForOrganizations = async (organizationIds?: number[]) => {
    const isForAllOrgs = !organizationIds
    const setLoading = isForAllOrgs ? setLoadingAllOrgs : setLoadingSpecificOrgs

    setLoading(true)
    try {
      await mutateAsync(
        {
          url: endpoints.calculateAllProviderRisks,
          body: organizationIds || null,
        },
        {
          onSuccess: () => {
            const messageId = isForAllOrgs
              ? 'topicalRisk.admin.calculateAllOrganizationsSuccess'
              : 'topicalRisk.admin.calculateSpecificOrganizationsSuccess'

            showSnackbar({
              message: formatMessage({ id: messageId }, { count: organizationIds?.length }),
              severity: NotificationSeverity.success,
            })
          },
          onError: () => {
            const messageId = isForAllOrgs
              ? 'topicalRisk.admin.calculateAllOrganizationsError'
              : 'topicalRisk.admin.calculateSpecificOrganizationsError'

            showSnackbar({
              message: formatMessage({ id: messageId }),
              severity: NotificationSeverity.error,
            })
          },
        },
      )
    } finally {
      setLoading(false)
    }
  }

  const handleRunForSpecificOrganizations = async () => {
    const organizationIds = selectedOrganizations.map(org => org.id)

    if (organizationIds.length === 0) {
      return
    }

    await handleRunForOrganizations(organizationIds)
  }

  return (
    <ConfigurationCollection enablePadding={{ top: false, right: true }}>
      <List>
        <ListItem
          secondaryAction={
            <LoadingButton
              variant="contained"
              size="large"
              onClick={() => handleRunForOrganizations()}
              loading={loadingAllOrgs}
            >
              {formatMessage({ id: 'topicalRisk.admin.runJob' })}
            </LoadingButton>
          }
        >
          <ListItemText
            primary={formatMessage({ id: 'topicalRisk.admin.runJobForAllOrganizations' })}
            secondary={formatMessage({ id: 'topicalRisk.admin.runJobForAllOrganizationsDescription' })}
          />
        </ListItem>

        <Divider sx={{ mt: 2, mb: 2 }} />

        <ListItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item sm={5}>
              <ListItemText
                primary={formatMessage({ id: 'topicalRisk.admin.runJobForSpecificOrganizations' })}
                secondary={formatMessage({ id: 'topicalRisk.admin.runJobForSpecificOrganizationsDescription' })}
              />
            </Grid>
            <Grid item sm sx={{ mr: 4 }}>
              <Autocomplete
                multiple
                options={organizations || []}
                disableListWrap
                disableCloseOnSelect
                clearOnBlur={false}
                getOptionLabel={option => `${option.id}: ${option.name}`}
                ListboxComponent={ListboxComponent}
                onChange={(_, value) => setSelectedOrganizations(value)}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'reset') {
                    setSearchInput(value)
                  }
                }}
                value={selectedOrganizations}
                loading={isLoadingOrganizations}
                disabled={loadingSpecificOrgs}
                filterOptions={x => x}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={
                        <>
                          <span style={{ fontFamily: 'monospace' }}>{option.id}</span>: {option.name}
                        </>
                      }
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={formatMessage({ id: 'topicalRisk.admin.organizationLabel' })}
                    placeholder={formatMessage({ id: 'topicalRisk.admin.organizationPlaceholder' })}
                    translate="no"
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <OptionIcon selected={selected} multiple />
                    <span style={{ fontFamily: 'monospace' }}>{option.id}</span>: {option.name}
                  </li>
                )}
              />
            </Grid>
            <Grid item sm="auto">
              <LoadingButton
                variant="contained"
                size="large"
                onClick={handleRunForSpecificOrganizations}
                loading={loadingSpecificOrgs}
                disabled={selectedOrganizations.length === 0 || loadingSpecificOrgs}
              >
                {formatMessage({ id: 'topicalRisk.admin.runJob' })}
              </LoadingButton>
            </Grid>
          </Grid>
        </ListItem>
      </List>
    </ConfigurationCollection>
  )
}

export default TopicalRiskConfig
